import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageTitle, Stat, Badge, Empty } from "@/components/app/ui";
import { api, errMsg } from "@/lib/api";

export const TrackingStats = ({ t }) => (
  <div className="grid gap-4 sm:grid-cols-4">
    <Stat label="Tracked clicks" value={t.total_clicks} color="#06b6d4" testId="track-clicks" />
    <Stat label="Form fills / leads" value={t.total_leads} color="#db2777" testId="track-leads" />
    <Stat label="Click → lead" value={`${t.conversion}%`} color="#10b981" testId="track-conversion" />
    <Stat label="Tracked content" value={t.tracked_items} color="#7c3aed" />
  </div>
);

export default function Leads() {
  const [t, setT] = useState(null);
  const [leads, setLeads] = useState([]);
  const load = () => { api.get("/app/tracking").then((r) => setT(r.data)); api.get("/app/leads").then((r) => setLeads(r.data)); };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { try { await api.patch(`/app/leads/${id}`, { status }); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!t) return <div className="h-64 rounded-2xl bg-muted animate-pulse" />;
  return (
    <div data-testid="leads-page">
      <PageTitle title="Leads & Tracking" copy="Every piece of content published through Sliance can carry a tracked link. Clicks and lead-form submissions are logged here, and the company owner gets an email for each new lead." />
      <TrackingStats t={t} />
      <div className="mt-6 card-box p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm"><span className="text-muted-foreground">Your hosted lead form:</span> <a href={t.lead_form_url} target="_blank" rel="noreferrer" className="font-medium text-[#7c3aed] break-all" data-testid="lead-form-url">{t.lead_form_url}</a></div>
        <button className="btn-ghost !py-2 text-xs" onClick={() => { navigator.clipboard.writeText(t.lead_form_url); toast.success("Copied"); }} data-testid="copy-form-url"><Copy className="h-3.5 w-3.5" /> Copy</button>
      </div>
      <h3 className="mt-10 mb-4 text-xl font-semibold">Performance by content</h3>
      <div className="card-box overflow-x-auto">
        <Table data-testid="tracking-table">
          <TableHeader><TableRow><TableHead>Content</TableHead><TableHead>Channel</TableHead><TableHead className="text-right">Clicks</TableHead><TableHead className="text-right">Leads</TableHead><TableHead className="text-right">Conversion</TableHead></TableRow></TableHeader>
          <TableBody>
            {t.rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Publish approved content with a tracked link to start measuring.</TableCell></TableRow>}
            {t.rows.map((r) => <TableRow key={r.content_id}><TableCell>{r.title}</TableCell><TableCell>{r.channel}</TableCell><TableCell className="text-right">{r.clicks}</TableCell><TableCell className="text-right">{r.leads}</TableCell><TableCell className="text-right">{r.conversion}%</TableCell></TableRow>)}
          </TableBody>
        </Table>
      </div>
      <h3 className="mt-10 mb-4 text-xl font-semibold">Leads inbox</h3>
      {leads.length === 0 ? <Empty title="No leads yet" copy="Leads from your tracked content and hosted form will appear here." /> : (
        <div className="space-y-3">{leads.map((l) => (
          <div key={l.id} className="card-box p-5" data-testid={`lead-${l.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div><div className="font-semibold">{l.name} · <a href={`mailto:${l.email}`} className="text-[#7c3aed]">{l.email}</a></div>
                <div className="text-xs text-muted-foreground">{l.channel} · {l.content_title || "Direct"} · {l.phone || "—"} · {l.company || "—"} · {new Date(l.created_at).toLocaleString()}</div></div>
              <div className="flex items-center gap-2">
                {l.intent_score != null && <span className="rounded-full bg-[#10b981]/15 px-2.5 py-1 text-[11px] font-semibold text-[#059669]" title={l.intent_reason}>Intent {l.intent_score}</span>}
                <Badge s={l.status} />
                <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}><SelectTrigger className="w-32 h-9" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["new", "contacted", "qualified", "won", "lost"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            {l.message && <p className="mt-3 text-sm whitespace-pre-wrap">{l.message}</p>}
          </div>
        ))}</div>
      )}
    </div>
  );
}
